import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { TeamRole } from '@/consts/team'
import { VOLUME_TYPE } from '@/models/volume'
import { useTeamUserRole } from '@/stores/team'
import { formatGB2Bytes, formatMB2Bytes } from '@/utils/formatter'
import { getEffectiveQuota } from '@/utils/volume'

interface UseTeamFSSpaceStatsOptions {
  teamResID?: string
}

interface FSSpaceData {
  usageBytes: number
  quotaBytes: number
}

/**
 * Hook to fetch team file system space statistics
 * Uses /team/info API (accessible by all roles) to get team shared datadrive stats
 * and attempts to fetch all members' stats via /team/stat/fs_spaces (Admin/Owner only)
 */
export function useTeamFSSpaceStats(options: UseTeamFSSpaceStatsOptions) {
  const { teamResID } = options
  const userRole = useTeamUserRole()

  const [teamShared, setTeamShared] = useState<FSSpaceData>({
    usageBytes: 0,
    quotaBytes: 0,
  })

  const [allMembers, setAllMembers] = useState<FSSpaceData>({
    usageBytes: 0,
    quotaBytes: 0,
  })

  const [loading, setLoading] = useState(false)

  const fetchStats = useCallback(async () => {
    if (!teamResID) {
      // Reset all states when no team is selected
      setTeamShared({ usageBytes: 0, quotaBytes: 0 })
      setAllMembers({ usageBytes: 0, quotaBytes: 0 })
      return
    }

    setLoading(true)
    try {
      // Fetch team info (accessible by all roles)
      const res = await API.getTeamInfo({ res_id: teamResID })

      if (res.code === 0 && res.team?.volumes?.subParts) {
        const volumes = res.team.volumes

        // Check expiration from volumes.expireTime
        let expired = false
        if (volumes.expireTime?.seconds) {
          expired = volumes.expireTime.seconds * 1000 < Date.now()
        }

        // Filter datadrive volumes (type = 1) from team shared subParts
        const datadriveVolumes = volumes.subParts.filter(
          (v: any) => v.type === VOLUME_TYPE.DATA_DRIVE,
        )

        // Calculate team shared datadrive stats by aggregating all regions
        // Use getEffectiveQuota for correct quota based on expiration
        const quotaBytes = datadriveVolumes.reduce(
          (sum: number, v: any) => sum + formatGB2Bytes(getEffectiveQuota(v, expired)),
          0,
        )
        const usageBytes = datadriveVolumes.reduce(
          (sum: number, v: any) => sum + (v.usage_bytes || formatMB2Bytes(v.usage_mb)),
          0,
        )

        setTeamShared({ usageBytes, quotaBytes })
      } else {
        setTeamShared({ usageBytes: 0, quotaBytes: 0 })
      }

      // Fetch all members' stats (only for Admin/Owner)
      if (userRole === TeamRole.Admin || userRole === TeamRole.Owner) {
        try {
          const memberStatsRes = await API.getTeamStatFSSpaces({ res_id: teamResID })
          setAllMembers({
            usageBytes: memberStatsRes.memberFSSpaceOverview.usageBytes,
            quotaBytes: memberStatsRes.memberFSSpaceOverview.quotaBytes,
          })
        } catch (memberError) {
          console.error('Failed to fetch member stats:', memberError)
          setAllMembers({ usageBytes: 0, quotaBytes: 0 })
        }
      } else {
        // Normal members don't have permission - skip API call
        setAllMembers({ usageBytes: 0, quotaBytes: 0 })
      }
    } catch (error) {
      console.error('Failed to fetch team FS space stats:', error)
      setTeamShared({ usageBytes: 0, quotaBytes: 0 })
      setAllMembers({ usageBytes: 0, quotaBytes: 0 })
    } finally {
      setLoading(false)
    }
  }, [teamResID, userRole])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    // Team shared datadrive data
    teamShared,
    // All members aggregate data
    allMembers,
    loading,
    refetch: fetchStats,
  }
}
